import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Zap } from "lucide-react";

export default function Login() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    if (user) return <Navigate to="/admin" replace />;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const res = await login(email.trim(), password);
        setLoading(false);
        if (res.ok) {
            toast.success("Welcome back");
            navigate("/admin", { replace: true });
        } else {
            toast.error(res.error || "Login failed");
        }
    };

    return (
        <div className="min-h-screen w-full bg-[#FAFAFA] flex items-stretch">
            <div className="hidden lg:flex flex-1 relative overflow-hidden bg-[#0F0F11] text-white">
                <div
                    className="absolute inset-0 opacity-40"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 20% 20%, rgba(255,90,31,0.4), transparent 45%), radial-gradient(circle at 80% 70%, rgba(255,90,31,0.25), transparent 40%)",
                    }}
                />
                <div className="relative z-10 flex flex-col justify-between p-12 w-full">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-xl bg-brand flex items-center justify-center">
                            <Zap className="h-5 w-5" strokeWidth={2.5} />
                        </div>
                        <span className="font-display text-2xl font-bold tracking-tight">DinePulse</span>
                    </div>
                    <div>
                        <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-6">Admin Console</div>
                        <h1 className="font-display text-5xl xl:text-6xl font-bold tracking-tight leading-[1.05]">
                            One tap.<br />Every link that matters.
                        </h1>
                        <p className="mt-6 text-white/60 max-w-md leading-relaxed">
                            Create beautiful NFC business pages in minutes. Program, tap, deliver — while the customer waits.
                        </p>
                    </div>
                    <div className="text-xs text-white/40">© {new Date().getFullYear()} DinePulse — private admin.</div>
                </div>
            </div>

            <div className="w-full lg:w-[520px] flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-sm">
                    <div className="lg:hidden mb-8 flex items-center gap-2">
                        <div className="h-9 w-9 rounded-xl bg-brand flex items-center justify-center text-white">
                            <Zap className="h-5 w-5" strokeWidth={2.5} />
                        </div>
                        <span className="font-display text-xl font-bold">DinePulse</span>
                    </div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Sign in</div>
                    <h2 className="mt-2 font-display text-3xl font-bold tracking-tight">DinePulse Admin</h2>
                    <p className="mt-2 text-sm text-ink-muted">Secure single-admin access.</p>

                    <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.15em] text-ink-muted">
                                Email
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                required
                                autoFocus
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="admin@dinepulse.app"
                                data-testid="login-email-input"
                                className="h-11"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.15em] text-ink-muted">
                                Password
                            </Label>
                            <Input
                                id="password"
                                type="password"
                                required
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                data-testid="login-password-input"
                                className="h-11"
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="login-submit-button"
                            className="w-full h-11 bg-brand hover:bg-brand-hover text-white font-medium active:scale-[0.98] transition-transform"
                        >
                            {loading ? "Signing in..." : "Sign in"}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
}
